import React, { useEffect, useMemo, useState } from 'react';
/* global axios */
import api from '../services/apiService';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import { useLocation } from 'react-router-dom';
import { formatDateDE } from '../utils/dateFormatter';

// Hilfsfunktionen
const toISO = (yyyy_mm_dd) => yyyy_mm_dd || null; // Input type="date" ist bereits YYYY-MM-DD
const money = (v) => (Number(v || 0).toFixed(2) + ' €').replace('.', ',');
const outstanding = (inv) => {
  const gross = Number(inv?.totalGross || 0);
  const paid  = Number(inv?.amountPaid || 0);
  return Math.max(0, gross - paid);
};

// ⬇️ Pane-Style analog zur Zahlungshistorie (Breite anpassen, wenn deine History anders ist)
const historyPanelStyle = {
  maxWidth: '100%',           // <- gleiche maxBreite wie deine Zahlungshistorie
  margin: '12px auto',     // zentriert unterhalb der Selects
  padding: 12,
  border: '1px solid #ddd',
  borderRadius: 8,
  background: '#fff',
};

const thCenter = { textAlign: 'center', padding: '6px 4px' };

const PAYMENT_METHOD_OPTIONS = [
  { value: 'bank_transfer', label: 'Überweisung' },
  { value: 'sepa', label: 'SEPA' },
  { value: 'cash', label: 'Bar' },
  { value: 'card', label: 'Karte' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'credit_note', label: 'Gutschrift' },
  { value: 'writeoff', label: 'Abschreibung' },
  { value: 'other', label: 'Sonstiges' }
];
const PAYMENT_STATUS_OPTIONS = [
  { value: 'posted', label: 'Verbucht' },
  { value: 'pending', label: 'Ausstehend' },
  { value: 'reversed', label: 'Storniert' },
];

const PaymentForm = () => {
  // Filter-Optionen vom Server (wie bei InvoiceList)
  const [serverFilterOptions, setServerFilterOptions] = useState({
    invoiceNumbers: [],
    customers: []
  });

  // Ausgewählte Rechnung (über Select)
  const [selectedInvoice, setSelectedInvoice] = useState(null); // {value, label, id?}
  const [invoiceSummary, setInvoiceSummary] = useState(null);   // GET /invoices/:id
  const [selectedCustomer, setSelectedCustomer] = useState(null); // {value,label}
  const [customerInvoices, setCustomerInvoices] = useState([]);   // Array von Invoices (id, invoiceNumber, ...)
  const location = useLocation(); // ⬅️ NEU
  // ⬇️ NEU: Offene Posten des gewählten Kunden
  const [openItems, setOpenItems] = useState([]);
  const [openTotal, setOpenTotal] = useState(0);
    // ⬅️ INSERT: Sperr-Status des Kunden
  const [customerBlock, setCustomerBlock] = useState({
    customerId: null,
    isBlocked: false,
    blockReason: '',
    blockedUntil: '', // yyyy-mm-dd
    loading: false,
    saving: false,
  });


  // Zahlungen der Rechnung
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Neues Zahlungsformular
  const [newPayment, setNewPayment] = useState({
    paymentDate: '',
    amount: '',
    currency: 'EUR',
    method: PAYMENT_METHOD_OPTIONS[0].value,
    status: 'posted',
    reference: '',
    transactionId: '',
    feeAmount: '',
    notes: ''
  });

  // Inline-Edit
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState(null);

  // Filterdaten holen (Rechnungsnummern etc.)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await api.get('/invoices/filter-options');
        const fo = res.data || {};
        setServerFilterOptions({
          invoiceNumbers: (fo.invoiceNumbers || []).slice().sort((a, b) => {
            const aNum = parseInt(String(a).replace(/\D/g, ''), 10);
            const bNum = parseInt(String(b).replace(/\D/g, ''), 10);
            if (!isNaN(aNum) && !isNaN(bNum)) return bNum - aNum;
            return String(b).localeCompare(String(a));
          }),
          customers: fo.customers || []
        });
      } catch (e) {
        console.error('Fehler beim Laden der Filteroptionen:', e);
      }
    };
    fetchFilterOptions();
  }, []);


  // ⬇️ NEU: Wenn Kunde gewählt, nur dessen Rechnungen laden
useEffect(() => {
  const fetchInvoicesForCustomer = async () => {
    setPayments([]);
    setInvoiceSummary(null);

    if (!selectedCustomer?.value) {
      setCustomerInvoices([]);
      setOpenItems([]);
      setOpenTotal(0);
       // ⬅️ INSERT: Sperre resetten, wenn kein Kunde gewählt
      setCustomerBlock({
        customerId: null, isBlocked: false, blockReason: '', blockedUntil: '',
        loading: false, saving: false
      });
      return;
    }
    try {
      const params = new URLSearchParams({
        customer: selectedCustomer.value,
        limit: 500, // etwas großzügiger
        page: 1,
      }).toString();
      const res = await api.get(`/invoices/list?${params}`);
      const list = res.data?.data || [];
      setCustomerInvoices(list);

      // ⬇️ NEU: offene Posten ermitteln (paymentState != 'paid' ODER Restbetrag > 0)
      const open = list
        .filter(inv => (inv.paymentState !== 'paid') || outstanding(inv) > 0)
        .map(inv => ({
          id: inv.id,
          invoiceNumber: inv.invoiceNumber,
          date: inv.date,
          totalGross: Number(inv.totalGross || 0),
          amountPaid: Number(inv.amountPaid || 0),
          lastPaymentDate: inv.lastPaymentDate || null,
          paymentState: inv.paymentState || 'unpaid',
          openAmount: outstanding(inv),
        }))
        // sinnvolle Sortierung: älteste zuerst, dann offene Summe absteigend
        .sort((a, b) => (new Date(a.date) - new Date(b.date)) || (b.openAmount - a.openAmount));

      setOpenItems(open);
      setOpenTotal(open.reduce((s, x) => s + x.openAmount, 0));

      // bestehende Auswahl invalidieren, falls sie nicht zum Kunden gehört
      if (selectedInvoice?.id && !list.some(inv => inv.id === selectedInvoice.id)) {
        setSelectedInvoice(null);
      }

         // ⬅️ INSERT: Kundensperre laden
      setCustomerBlock(prev => ({ ...prev, loading: true }));
      try {
        const meta = await fetchCustomerMetaByCompany(selectedCustomer.value);
        setCustomerBlock({
          customerId: meta?.id ?? null,
          isBlocked: !!meta?.isBlocked,
          blockReason: meta?.blockReason ?? '',
          blockedUntil: meta?.blockedUntil ? String(meta.blockedUntil).slice(0,10) : '',
          loading: false,
          saving: false,
        });
      } catch (e) {
        console.error('Sperrstatus laden fehlgeschlagen:', e);
        setCustomerBlock(prev => ({ ...prev, loading: false }));
      }
    } catch (e) {
      console.error('Fehler beim Laden der Kundenrechnungen:', e);
      setCustomerInvoices([]);
      setOpenItems([]);
      setOpenTotal(0);
       // ⬅️ INSERT: bei Fehler Sperre resetten
      setCustomerBlock({
        customerId: null, isBlocked: false, blockReason: '', blockedUntil: '',
        loading: false, saving: false
      });
    }
  };
  fetchInvoicesForCustomer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedCustomer]);



// ⬇️ NEU: /payments?invoiceId=123 ODER ?invoiceNumber=RE-00123 automatisch auswählen
useEffect(() => {
  const params = new URLSearchParams(location.search);
  const qId = params.get('invoiceId');
  const qNumber = params.get('invoiceNumber');

  const init = async () => {
    if (qId) {
      // Nur ID bekannt: Lade Invoice, setze Nummer + Kunde
      try {
        const invRes = await api.get(`/invoices/${qId}`);
        const inv = invRes.data;
        const invNo = inv?.invoiceNumber || '';
        const cust = inv?.customer?.company || '';

        setSelectedInvoice({ id: Number(qId), value: invNo, label: invNo });

        // Kunde im Kunden-Select vorbesetzen (triggert das Laden der Kundenrechnungen)
        if (cust) setSelectedCustomer({ value: cust, label: cust });
      } catch (e) {
        console.error('Fehler beim Laden der Invoice aus Query:', e);
      }
      return;
    }

    if (qNumber) {
      try {
        const inv = await resolveInvoiceByNumber(qNumber);
        if (inv) {
          setSelectedInvoice({ id: inv.id, value: inv.invoiceNumber, label: inv.invoiceNumber });
          const cust = inv?.customer?.company || '';
          if (cust) setSelectedCustomer({ value: cust, label: cust });
        }
      } catch (e) {
        console.error('Fehler beim Auflösen invoiceNumber aus Query:', e);
      }
    }
  };
  init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.search]);


  // Wenn Rechnung ausgewählt → Zahlungen + Summary laden
  useEffect(() => {
  const fetchData = async () => {
    if (!selectedInvoice?.id) {
      setPayments([]);
      setInvoiceSummary(null);
      return;
    }
    try {
      setLoadingPayments(true);
      const [payRes, invRes] = await Promise.all([
        api.get(`/payments/by-invoice/${selectedInvoice.id}`),
        api.get(`/invoices/${selectedInvoice.id}`)
      ]);
      setPayments(payRes.data || []);
      setInvoiceSummary(invRes.data || null);

      // ⬇️ NEU: falls wir nur die ID gesetzt hatten, Label/Number nachziehen
      const invNo = invRes?.data?.invoiceNumber;
      if (invNo && (!selectedInvoice.label || !selectedInvoice.value)) {
        setSelectedInvoice(prev => prev ? { ...prev, value: invNo, label: invNo } : prev);
      }
    } catch (e) {
      console.error('Fehler beim Laden der Zahlungen/Invoice:', e);
    } finally {
      setLoadingPayments(false);
    }
  };
  fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedInvoice?.id]);


  // Ableitungen
  const invoiceSelectOptions = useMemo(() => {
  let opts = [];

  if (selectedCustomer?.value && Array.isArray(customerInvoices)) {
    opts = customerInvoices
      .slice()
      .sort((a, b) => {
        const aNum = parseInt(String(a.invoiceNumber || '').replace(/\D/g, ''), 10);
        const bNum = parseInt(String(b.invoiceNumber || '').replace(/\D/g, ''), 10);
        if (!isNaN(aNum) && !isNaN(bNum)) return bNum - aNum;
        return String(b.invoiceNumber || '').localeCompare(String(a.invoiceNumber || ''));
      })
      .map(inv => ({
        value: inv.invoiceNumber,
        label: inv.invoiceNumber,
        _id: inv.id
      }));
  } else {
    opts = (serverFilterOptions.invoiceNumbers || []).map(v => ({ value: v, label: v }));
  }

  // ⬇️ sicherstellen, dass die Auswahl vorhanden ist
  if (selectedInvoice?.value && !opts.some(o => o.value === selectedInvoice.value)) {
    opts = [{ value: selectedInvoice.value, label: selectedInvoice.label, _id: selectedInvoice.id }, ...opts];
  }

  return opts;
}, [selectedCustomer, customerInvoices, serverFilterOptions, selectedInvoice]);




  // Hilfsfunktion: Rechnung anhand Rechnungsnummer auflösen
  const resolveInvoiceByNumber = async (invoiceNumber) => {
    // Wir verwenden deinen bestehenden Endpunkt /invoices/list?invoiceNumber=...
    const query = new URLSearchParams({ invoiceNumber, limit: 1, page: 1 }).toString();
    const res = await api.get(`/invoices/list?${query}`);
    const first = res.data?.data?.[0];
    return first || null;
  };

    // ⬅️ INSERT: Kunden-Meta (ID  Sperre) per Firmenname laden
  const fetchCustomerMetaByCompany = async (companyName) => {
    // Variante A: eigener schlanker Endpoint /customers/lookup?company=
    // Variante B: /customers/list?search= ... (je nachdem, was du hast)
    // Hier Beispiel A – passe die URL ggf. an deinen Backend-Route-Namen an:
    const res = await api.get(`/customers/lookup?company=${encodeURIComponent(companyName)}`);
    // Erwartet: { id, isBlocked, blockReason, blockedUntil }
    return res.data;
  };

  // Auswahländerung im Select
  const handleSelectInvoice = async (sel) => {
  if (!sel) {
    setSelectedInvoice(null);
    return;
  }
  // Wenn Option bereits die ID enthält (Kundenfilterpfad)
  if (sel._id) {
    setSelectedInvoice({ id: sel._id, value: sel.value, label: sel.label });
    return;
  }
  // Fallback: per Rechnungsnummer auflösen (globaler Pfad)
  try {
    const inv = await resolveInvoiceByNumber(sel.value);
    if (!inv) {
      alert('Rechnung nicht gefunden.');
      setSelectedInvoice(null);
      return;
    }
    setSelectedInvoice({ id: inv.id, value: inv.invoiceNumber, label: inv.invoiceNumber });
  } catch (e) {
    console.error(e);
    setSelectedInvoice(null);
  }
};


  // ⬅️ INSERT: Sperre speichern
  const handleSaveCustomerBlock = async () => {
    if (!customerBlock.customerId) {
      alert('Kein Kunde ausgewählt.');
      return;
    }
    try {
      setCustomerBlock(prev => ({ ...prev, saving: true }));
      await api.put(`/customers/${customerBlock.customerId}/block`, {
        isBlocked: !!customerBlock.isBlocked,
        blockReason: customerBlock.blockReason || null,
        blockedUntil: customerBlock.blockedUntil || null, // 'YYYY-MM-DD' oder null
      });
      alert('Kundensperre gespeichert.');
    } catch (e) {
      console.error('Kundensperre speichern fehlgeschlagen:', e);
      const msg = e.response?.data?.error || 'Fehler beim Speichern der Kundensperre.';
      alert(msg);
    } finally {
      setCustomerBlock(prev => ({ ...prev, saving: false }));
    }
  };

  // Neue Zahlung speichern
  const handleCreatePayment = async () => {
    if (!selectedInvoice?.id) {
      alert('Bitte zuerst eine Rechnung auswählen.');
      return;
    }
    const payload = {
      invoiceId: selectedInvoice.id,
      paymentDate: toISO(newPayment.paymentDate),
      amount: newPayment.amount ? Number(newPayment.amount) : 0,
      currency: newPayment.currency || 'EUR',
      method: newPayment.method,
      status: newPayment.status,
      reference: newPayment.reference || undefined,
      transactionId: newPayment.transactionId || undefined,
      feeAmount: newPayment.feeAmount ? Number(newPayment.feeAmount) : undefined,
      notes: newPayment.notes || undefined
    };
    if (!payload.paymentDate) return alert('Bitte Zahlungsdatum setzen (YYYY-MM-DD).');
    if (!payload.amount || isNaN(payload.amount)) return alert('Bitte einen gültigen Betrag angeben.');

    try {
      const res = await api.post('/payments', payload);
      // Re-Load
      const payRes = await api.get(`/payments/by-invoice/${selectedInvoice.id}`);
      setPayments(payRes.data || []);
      // Summary aktualisieren (kommt auch in POST response zurück, aber wir laden sicherheitshalber frisch)
      const invRes = await api.get(`/invoices/${selectedInvoice.id}`);
      setInvoiceSummary(invRes.data || null);

      // Formular reset
      setNewPayment({
        paymentDate: '',
        amount: '',
        currency: 'EUR',
        method: PAYMENT_METHOD_OPTIONS[0].value,
        status: 'posted',
        reference: '',
        transactionId: '',
        feeAmount: '',
        notes: ''
      });
      alert('Zahlung gespeichert.');
    } catch (e) {
      console.error('Fehler beim Anlegen der Zahlung:', e);
      const msg = e.response?.data?.error || 'Fehler beim Anlegen.';
      alert(msg);
    }
  };

  // Inline-Edit aktivieren
  const startEdit = (p) => {
    setEditingId(p.id);
    setEditRow({
      paymentDate: (p.paymentDate || '').slice(0, 10), // yyyy-mm-dd
      amount: String(p.amount ?? ''),
      currency: p.currency || 'EUR',
      method: p.method || 'bank_transfer',
      status: p.status || 'posted',
      reference: p.reference || '',
      transactionId: p.transactionId || '',
      feeAmount: p.feeAmount != null ? String(p.feeAmount) : '',
      notes: p.notes || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditRow(null);
  };

  // Inline-Edit speichern
  const saveEdit = async (id) => {
    if (!selectedInvoice?.id) return;
    const payload = {
      paymentDate: toISO(editRow.paymentDate),
      amount: editRow.amount ? Number(editRow.amount) : 0,
      currency: editRow.currency || 'EUR',
      method: editRow.method,
      status: editRow.status,
      reference: editRow.reference || undefined,
      transactionId: editRow.transactionId || undefined,
      feeAmount: editRow.feeAmount ? Number(editRow.feeAmount) : undefined,
      notes: editRow.notes || undefined
    };
    if (!payload.paymentDate) return alert('Bitte Zahlungsdatum setzen (YYYY-MM-DD).');
    if (!payload.amount || isNaN(payload.amount)) return alert('Bitte einen gültigen Betrag angeben.');

    try {
      await api.put(`/payments/${id}`, payload);
      // Re-Load
      const [payRes, invRes] = await Promise.all([
        api.get(`/payments/by-invoice/${selectedInvoice.id}`),
        api.get(`/invoices/${selectedInvoice.id}`)
      ]);
      setPayments(payRes.data || []);
      setInvoiceSummary(invRes.data || null);
      cancelEdit();
      alert('Zahlung aktualisiert.');
    } catch (e) {
      console.error('Fehler beim Aktualisieren:', e);
      const msg = e.response?.data?.error || 'Fehler beim Aktualisieren.';
      alert(msg);
    }
  };

  // Zahlung löschen
  const removePayment = async (id) => {
    if (!selectedInvoice?.id) return;
    if (!window.confirm('Zahlung wirklich löschen?')) return;
    try {
      await api.delete(`/payments/${id}`);
      const [payRes, invRes] = await Promise.all([
        api.get(`/payments/by-invoice/${selectedInvoice.id}`),
        api.get(`/invoices/${selectedInvoice.id}`)
      ]);
      setPayments(payRes.data || []);
      setInvoiceSummary(invRes.data || null);
      alert('Zahlung gelöscht.');
    } catch (e) {
      console.error('Fehler beim Löschen:', e);
      alert('Fehler beim Löschen der Zahlung.');
    }
  };

  // Badge für Payment State
  const PaymentStateBadge = ({ state }) => {
    const map = {
      unpaid: { bg: '#dc3545', txt: 'Unbezahlt' },
      partial: { bg: '#ffc107', txt: 'Teilzahlung' },
      paid: { bg: '#28a745', txt: 'Bezahlt' }
    };
    const item = map[state] || { bg: '#6c757d', txt: state || '-' };
    return (
      <span style={{ background: item.bg, color: '#fff', padding: '3px 8px', borderRadius: '6px', fontSize: 12 }}>
        {item.txt}
      </span>
    );
  };

  return (
    <div className="payment-form">
      <Helmet>
        <title>Zahlungen</title>
        <meta name="description" content="Zahlungseingänge verwalten" />
      </Helmet>

      <h1>Zahlungen</h1>

      {/* Rechnungsauswahl */}
      <div style={{ marginBottom: 20 }}>
  <h3>Rechnung wählen</h3>

  {/* ⬇️ NEU: Kundenfilter */}
  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, maxWidth: 480, marginBottom: 10 }}>
    <Select
      options={(serverFilterOptions.customers || []).map(v => ({ value: v, label: v }))}
      placeholder="Kunde (optional)"
      isClearable
      value={selectedCustomer}
      onChange={(opt) => setSelectedCustomer(opt || null)}
      styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
    />
  </div>

  {/* Rechnungsnummer (abhängig vom Kundenfilter) */}
  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, maxWidth: 480 }}>
    <Select
  options={invoiceSelectOptions}
  placeholder={selectedCustomer ? 'Rechnung dieses Kunden wählen' : 'Rechnungsnummer wählen'}
  isClearable
  value={
    selectedInvoice?.value
      ? { value: selectedInvoice.value, label: selectedInvoice.label, _id: selectedInvoice.id }
      : null
  }
  onChange={handleSelectInvoice}
  styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
/>



  </div>
</div>

{/* ⬇️ NEU: Panel Kundensperre */}
{selectedCustomer?.value && (
  <div style={{ ...historyPanelStyle, borderColor: '#f0b429', background: '#fffaf2' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h3 style={{ margin: 0 }}>Kundensperre für Aufträge</h3>
      {customerBlock.loading && <span style={{ fontStyle: 'italic' }}>lädt…</span>}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr auto', gap: 10, marginTop: 10, alignItems: 'center' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="checkbox"
          checked={customerBlock.isBlocked}
          onChange={e => setCustomerBlock(prev => ({ ...prev, isBlocked: e.target.checked }))}
        />
        Kunde gesperrt
      </label>
      <div>
        <label>Grund</label>
        <input
          type="text"
          value={customerBlock.blockReason}
          onChange={e => setCustomerBlock(prev => ({ ...prev, blockReason: e.target.value }))}
          placeholder="z. B. Zahlungsverzug"
          style={{ width: '100%', padding: 6 }}
          disabled={!customerBlock.isBlocked}
        />
      </div>
      <div>
        <label>Gesperrt bis</label>
        <input
          type="date"
          value={customerBlock.blockedUntil}
          onChange={e => setCustomerBlock(prev => ({ ...prev, blockedUntil: e.target.value }))}
          style={{ width: '100%', padding: 6 }}
          disabled={!customerBlock.isBlocked}
        />
      </div>
      <div>
        <button
          onClick={handleSaveCustomerBlock}
          disabled={customerBlock.saving || !customerBlock.customerId}
          style={{
            padding: '8px 12px',
            background: '#f0b429',
            color: '#000',
            border: '1px solid #e0a800',
            borderRadius: 6,
            cursor: 'pointer',
            opacity: customerBlock.saving ? 0.7 : 1
          }}
          title="Sperre speichern"
        >
          {customerBlock.saving ? 'Speichere…' : 'Sperre speichern'}
        </button>
      </div>
    </div>
    {customerBlock.isBlocked && (
      <div style={{ marginTop: 8, fontSize: 12 }}>
        Neue <strong>Aufträge</strong> sind für diesen Kunden blockiert. Bestehende Aufträge bleiben unverändert.
      </div>
    )}
  </div>
)}


{/* ================= Offene Posten des Kunden ================= */}
{selectedCustomer?.value && (
  <div style={historyPanelStyle}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <h3 style={{ margin: 0 }}>Offene Posten – {selectedCustomer.value}</h3>
      <div style={{ fontWeight: 'bold' }}>
        Summe offen: {money(openTotal)}
      </div>
    </div>

    {openItems.length === 0 ? (
      <div style={{ marginTop: 8, fontStyle: 'italic' }}>Keine offenen Posten 🎉</div>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
       <thead>
  <tr>
    <th style={thCenter}>Rech.-Nr.</th>
    <th style={thCenter}>Datum</th>
    <th style={thCenter}>Brutto</th>
    <th style={thCenter}>Bezahlt</th>
    <th style={thCenter}>Offen</th>
    <th style={thCenter}>Status</th>
    <th style={thCenter}>Letzte Zahlung</th>
    <th style={thCenter}></th>
  </tr>
</thead>
        <tbody>
          {openItems.map(row => (
            <tr key={row.id} style={{ borderTop: '1px solid #eee' }}>
              <td style={{ padding: '6px 4px' }}>{row.invoiceNumber || '-'}</td>
              <td style={{ padding: '6px 4px' }}>
                {formatDateDE(row.date, '-')}
              </td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>{money(row.totalGross)}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right' }}>{money(row.amountPaid)}</td>
              <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600 }}>
                {money(row.openAmount)}
              </td>
              <td style={{ padding: '6px 4px' }}>
                {row.paymentState === 'paid' ? 'Bezahlt' : row.paymentState === 'partial' ? 'Teilzahlung' : 'Unbezahlt'}
              </td>
              <td style={{ padding: '6px 4px' }}>
                {formatDateDE(row.lastPaymentDate, '-')}
              </td>
              <td style={{ padding: '6px 4px' }}>
                <button
                  onClick={() => setSelectedInvoice({ id: row.id, value: row.invoiceNumber, label: row.invoiceNumber })}
                  title="Diese Rechnung in der Zahlungserfassung öffnen"
                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc', cursor: 'pointer' }}
                >
                  auswählen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
)}

      {/* Zusammenfassung zur Rechnung */}
      {invoiceSummary && (
        <div
          style={{
            marginBottom: 20,
            padding: 12,
            border: '1px solid #ddd',
            borderRadius: 8,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 12
          }}
        >
          <div><strong>Rechnung:</strong> {invoiceSummary.invoiceNumber || '-'}</div>
          <div><strong>Brutto gesamt:</strong> {money(invoiceSummary.totalGross)}</div>
          <div><strong>Bereits bezahlt:</strong> {money(invoiceSummary.amountPaid)}</div>
          <div>
            <strong>Restbetrag:</strong>{' '}
            {money(Number(invoiceSummary.totalGross || 0) - Number(invoiceSummary.amountPaid || 0))}
          </div>
          <div><strong>Status:</strong> <PaymentStateBadge state={invoiceSummary.paymentState} /></div>
          <div><strong>Bezahlt am:</strong> {formatDateDE(invoiceSummary.paidDate, '-')}</div>
          <div><strong>Letzte Zahlung:</strong> {formatDateDE(invoiceSummary.lastPaymentDate, '-')}</div>
          <div><strong>Rechnungsdatum:</strong> {formatDateDE(invoiceSummary.date, '-')}</div>
        </div>
      )}

      {/* Formular: neue Zahlung */}
      <div style={{ marginBottom: 24 }}>
        <h3>Neue Zahlung erfassen</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <div>
            <label>Datum</label>
            <input
              type="date"
              value={newPayment.paymentDate}
              onChange={e => setNewPayment(p => ({ ...p, paymentDate: e.target.value }))}
              style={{ width: '100%', padding: 6 }}
            />
          </div>
          <div>
            <label>Betrag (€)</label>
            <input
              type="number"
              step="0.01"
              value={newPayment.amount}
              onChange={e => setNewPayment(p => ({ ...p, amount: e.target.value }))}
              style={{ width: '100%', padding: 6 }}
            />
          </div>
          <div>
            <label>Methode</label>
            <Select
              options={PAYMENT_METHOD_OPTIONS}
              value={PAYMENT_METHOD_OPTIONS.find(o => o.value === newPayment.method)}
              onChange={opt => setNewPayment(p => ({ ...p, method: opt?.value || 'bank_transfer' }))}
              styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
            />
          </div>
          <div>
            <label>Status</label>
            <Select
              options={PAYMENT_STATUS_OPTIONS}
              value={PAYMENT_STATUS_OPTIONS.find(o => o.value === newPayment.status)}
              onChange={opt => setNewPayment(p => ({ ...p, status: opt?.value || 'posted' }))}
              styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
            />
          </div>
          <div>
            <label>Referenz</label>
            <input
              type="text"
              value={newPayment.reference}
              onChange={e => setNewPayment(p => ({ ...p, reference: e.target.value }))}
              style={{ width: '100%', padding: 6 }}
            />
          </div>
          <div>
            <label>Transaktions-ID</label>
            <input
              type="text"
              value={newPayment.transactionId}
              onChange={e => setNewPayment(p => ({ ...p, transactionId: e.target.value }))}
              style={{ width: '100%', padding: 6 }}
            />
          </div>
          <div>
            <label>Gebühr (€)</label>
            <input
              type="number"
              step="0.01"
              value={newPayment.feeAmount}
              onChange={e => setNewPayment(p => ({ ...p, feeAmount: e.target.value }))}
              style={{ width: '100%', padding: 6 }}
            />
          </div>
          <div>
            <label>Notiz</label>
            <input
              type="text"
              value={newPayment.notes}
              onChange={e => setNewPayment(p => ({ ...p, notes: e.target.value }))}
              style={{ width: '100%', padding: 6 }}
            />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <button
            onClick={handleCreatePayment}
            style={{
              fontSize: 15,
              padding: '8px 12px',
              background: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer'
            }}
          >
            Zahlung speichern
          </button>
        </div>
      </div>

      {/* Tabelle: vorhandene Zahlungen */}
      <h3>Zahlungshistorie</h3>
      {loadingPayments ? (
        <p>Lade Zahlungen…</p>
      ) : payments.length === 0 ? (
        <p>Keine Zahlungen vorhanden.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Datum</th>
              <th>Betrag</th>
              <th>Währung</th>
              <th>Methode</th>
              <th>Status</th>
              <th>Referenz</th>
              <th>Transaktions-ID</th>
              <th>Gebühr</th>
              <th>Notiz</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {payments.map(p => {
              const isEditing = editingId === p.id;
              return (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editRow.paymentDate}
                        onChange={e => setEditRow(r => ({ ...r, paymentDate: e.target.value }))}
                        style={{ width: '100%', padding: 4 }}
                      />
                    ) : (
                      formatDateDE(p.paymentDate)
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editRow.amount}
                        onChange={e => setEditRow(r => ({ ...r, amount: e.target.value }))}
                        style={{ width: '100%', padding: 4 }}
                      />
                    ) : (
                      money(p.amount)
                    )}
                  </td>
                  <td>{isEditing ? (
                    <input
                      type="text"
                      value={editRow.currency}
                      onChange={e => setEditRow(r => ({ ...r, currency: e.target.value }))}
                      style={{ width: '100%', padding: 4 }}
                    />
                  ) : (p.currency || 'EUR')}</td>
                  <td>
                    {isEditing ? (
                      <Select
                        options={PAYMENT_METHOD_OPTIONS}
                        value={PAYMENT_METHOD_OPTIONS.find(o => o.value === editRow.method)}
                        onChange={opt => setEditRow(r => ({ ...r, method: opt?.value || 'bank_transfer' }))}
                        styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
                      />
                    ) : (
                      (PAYMENT_METHOD_OPTIONS.find(o => o.value === p.method)?.label || p.method)
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <Select
                        options={PAYMENT_STATUS_OPTIONS}
                        value={PAYMENT_STATUS_OPTIONS.find(o => o.value === editRow.status)}
                        onChange={opt => setEditRow(r => ({ ...r, status: opt?.value || 'posted' }))}
                        styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
                      />
                    ) : (
                      (PAYMENT_STATUS_OPTIONS.find(o => o.value === p.status)?.label || p.status)
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editRow.reference}
                        onChange={e => setEditRow(r => ({ ...r, reference: e.target.value }))}
                        style={{ width: '100%', padding: 4 }}
                      />
                    ) : (p.reference || '-')}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editRow.transactionId}
                        onChange={e => setEditRow(r => ({ ...r, transactionId: e.target.value }))}
                        style={{ width: '100%', padding: 4 }}
                      />
                    ) : (p.transactionId || '-')}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editRow.feeAmount}
                        onChange={e => setEditRow(r => ({ ...r, feeAmount: e.target.value }))}
                        style={{ width: '100%', padding: 4 }}
                      />
                    ) : (p.feeAmount != null ? money(p.feeAmount) : '-')}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editRow.notes}
                        onChange={e => setEditRow(r => ({ ...r, notes: e.target.value }))}
                        style={{ width: '100%', padding: 4 }}
                      />
                    ) : (p.notes || '-')}
                  </td>
                  <td>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => saveEdit(p.id)}
                          style={{ padding: '6px 10px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                        >
                          Speichern
                        </button>
                        <button
                          onClick={cancelEdit}
                          style={{ padding: '6px 10px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => startEdit(p)}
                          style={{ padding: '6px 10px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => removePayment(p.id)}
                          style={{ padding: '6px 10px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                        >
                          Löschen
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PaymentForm;
